/**
 *    SPDX-License-Identifier: Apache-2.0
 */

import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Row, Col } from 'reactstrap';
import FontAwesome from 'react-fontawesome';
import Card from '@material-ui/core/Card';
import Avatar from '@material-ui/core/Avatar';
import Dialog from '@material-ui/core/Dialog';
import ChartStats from '../Charts/ChartStats';
import TimelineStream from '../Lists/TimelineStream';
import ReactTable from '../Styled/Table';
import TransactionView from '../View/TransactionView';
import BlockView from '../View/BlockView';
import {
	blockListSearchType,
	dashStatsType,
	peerListType,
	txnListType,
	blockSearchType,
	transactionByOrgType,
	transactionListType
} from '../types';
import { connect } from 'react-redux';
import { currentChannelSelector } from '../../state/redux/charts/selectors';
import { tableOperations } from '../../state/redux/tables';

const { txnList, blockSearch, transactionList } = tableOperations;

/* istanbul ignore next */
const styles = theme => {
	const { type } = theme.palette;
	const dark = type === 'dark';
	return {
		background: {
			backgroundColor: dark ? 'rgb(36, 32, 54)' : '#f0f5f9'
		},
		view: {
			paddingTop: 85,
			paddingLeft: 0,
			paddingBottom: 120,
			width: '90%',
			maxWidth: '1400px',
			marginLeft: 'auto',
			marginRight: 'auto'
		},
		searchContainer: {
			backgroundColor: dark ? '#3c3558' : '#f0f5f9',
			padding: '30px 20px',
			borderRadius: '8px',
			marginBottom: '30px'
		},
		searchBar: {
			display: 'flex',
			alignItems: 'center',
			backgroundColor: '#ffffff',
			borderRadius: '8px',
			padding: '0',
			boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
			maxWidth: '800px',
			margin: '0 auto'
		},
		searchIcon: {
			padding: '0 15px',
			color: '#666666',
			fontSize: '20px'
		},
		searchInput: {
			flex: 1,
			border: 'none',
			outline: 'none',
			padding: '15px 10px',
			fontSize: '16px',
			'&::placeholder': {
				color: '#999999'
			}
		},
		searchButton: {
			backgroundColor: '#4A90E2',
			color: '#ffffff',
			border: 'none',
			padding: '15px 30px',
			borderRadius: '0 8px 8px 0',
			fontSize: '16px',
			fontWeight: '600',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: '#357ABD'
			}
		},
		dashboardSearch: {
			position: 'absolute',
			width: '80%'
		},
		search: {
			marginLeft: '10px'
		},
		statsGrid: {
			display: 'grid',
			gridTemplateColumns: 'repeat(4, 1fr)',
			gap: '20px',
			marginBottom: '30px',
			[theme.breakpoints.down('md')]: {
				gridTemplateColumns: 'repeat(2, 1fr)'
			},
			[theme.breakpoints.down('sm')]: {
				gridTemplateColumns: '1fr'
			}
		},
		statCard: {
			backgroundColor: dark ? '#453e68' : '#ffffff',
			borderRadius: '8px',
			padding: '25px',
			boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
			textAlign: 'center',
			transition: 'transform 0.2s',
			'&:hover': {
				transform: 'translateY(-2px)',
				boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
			}
		},
		statValue: {
			fontSize: '32px',
			fontWeight: 'bold',
			color: dark ? '#ffffff' : '#333333',
			marginBottom: '10px',
			lineHeight: '1.2'
		},
		statLabel: {
			fontSize: '14px',
			color: dark ? '#cccccc' : '#666666',
			fontWeight: '500',
			textTransform: 'uppercase',
			letterSpacing: '0.5px'
		},
		count: {
			color: dark ? '#ffffff' : undefined,
			[theme.breakpoints.only('xs')]: {
				marginTop: '10% !important'
			},
			[theme.breakpoints.up('xs')]: {
				marginTop: '55%'
			}
		},
		statistic: {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			display: 'block',
			float: 'left',
			height: '100%',
			width: '25%',
			textAlign: 'center',
			color: dark ? '#ffffff' : '#000000',
			[theme.breakpoints.only('xs')]: {
				fontSize: '8pt !important'
			},
			[theme.breakpoints.down('md')]: {
				fontSize: '12pt'
			},
			[theme.breakpoints.up('md')]: {
				fontSize: '18pt'
			}
		},
		vdivide: {
			'&::after': {
				borderRight: `2px ${dark ? 'rgb(40, 36, 61)' : '#dff1fe'} solid`,
				display: 'block',
				height: '45%',
				bottom: '55%',
				content: "' '",
				position: 'relative'
			}
		},
		avatar: {
			justifyContent: 'center',
			[theme.breakpoints.only('xs')]: {
				marginLeft: '35% !important',
				marginTop: '25% !important'
			},
			[theme.breakpoints.up('xs')]: {
				marginLeft: '60%',
				marginTop: '65%'
			}
		},
		node: {
			color: dark ? '#183a37' : '#21295c',
			backgroundColor: dark ? 'rgb(104, 247, 235)' : '#858aa6'
		},
		block: {
			color: dark ? '#1f1a33' : '#004d6b',
			backgroundColor: dark ? 'rgb(106, 156, 248)' : '#b9d6e1'
		},
		chaincode: {
			color: dark ? 'rgb(121, 83, 109)' : '#407b20',
			backgroundColor: dark ? 'rgb(247, 205, 234)' : '#d0ecda'
		},
		transaction: {
			color: dark ? 'rgb(216, 142, 4)' : '#ffa686',
			backgroundColor: dark ? 'rgb(252, 224, 174)' : '#ffeed8'
		},
		section: {
			minWidth: 280,
			height: 335,
			marginBottom: '2%',
			color: dark ? '#ffffff' : undefined,
			backgroundColor: dark ? '#3c3558' : undefined
		},
		center: {
			textAlign: 'center'
		},
		pie: {
			[theme.breakpoints.up('md')]: {
				marginLeft: '25%'
			}
		},
		transactionTable: {
			marginTop: '20px',
			backgroundColor: dark ? '#3c3558' : '#ffffff',
			padding: '20px',
			borderRadius: '4px',
			width: '100%',
			boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
		},
		tableWrapper: {
			width: '100%',
			overflowX: 'auto'
		},
		tableTitle: {
			fontSize: '16px',
			fontWeight: '600',
			marginBottom: '15px',
			color: dark ? '#ffffff' : '#333333',
			paddingBottom: '10px',
			borderBottom: `1px solid ${dark ? '#4a4a4a' : '#e0e0e0'}`
		},
		transactionLink: {
			color: dark ? '#90caf9' : '#4A90E2',
			textDecoration: 'none',
			'&:hover': {
				textDecoration: 'underline'
			}
		},
		tableStyles: {
			'& .rt-table': {
				width: '100% !important'
			},
			'& .rt-thead': {
				backgroundColor: dark ? '#4a4a4a' : '#E0F2F7 !important',
				'& .rt-th': {
					backgroundColor: dark ? '#4a4a4a' : '#E0F2F7 !important',
					color: dark ? '#ffffff' : '#333333 !important',
					fontWeight: '600',
					fontSize: '13px',
					padding: '12px 16px !important',
					textAlign: 'left !important',
					borderRight: `1px solid ${dark ? '#5a5a5a' : '#d0e0e5'} !important`,
					'&:last-child': {
						borderRight: 'none !important'
					}
				}
			},
			'& .rt-tbody': {
				'& .rt-tr-group': {
					borderBottom: 'none !important',
					'&:nth-child(even) .rt-tr': {
						backgroundColor: dark ? '#3a3a3a' : '#f9f9f9 !important'
					},
					'&:nth-child(odd) .rt-tr': {
						backgroundColor: dark ? '#2a2a2a' : '#ffffff !important'
					},
					'& .rt-tr:hover': {
						backgroundColor: dark ? '#4a4a4a' : '#f0f8ff !important'
					},
					'& .rt-td': {
						padding: '12px 16px !important',
						fontSize: '13px',
						borderRight: 'none !important',
						borderBottom: 'none !important',
						textAlign: 'left !important'
					}
				}
			}
		}
	};
};

export class DashboardView extends Component {
	constructor(props) {
		super(props);
		this.state = {
			notifications: [],
			hasDbError: false,
			searchValue: '',
			searchDialogOpen: false,
			searchError: ''
		};
	}

	formatNumber = (num) => {
		if (typeof num === 'string') {
			num = parseInt(num) || 0;
		}
		return num.toLocaleString('en-US');
	};

	componentWillMount() {
		const {
			blockListSearch,
			dashStats,
			peerList,
			transactionByOrg,
			blockActivity
		} = this.props;
		if (
			blockListSearch === undefined ||
			dashStats === undefined ||
			peerList === undefined ||
			blockActivity === undefined ||
			transactionByOrg === undefined
		) {
			this.setState({ hasDbError: true });
		}
	}

	componentDidMount() {
		const { blockActivity, currentChannel, getTransactionList } = this.props;
		this.setNotifications(blockActivity);
		
		// Fetch transaction list for recent transactions
		if (currentChannel && getTransactionList) {
			getTransactionList(currentChannel, { page: 1, size: 20 });
		}
	}

	componentWillReceiveProps() {
		const { blockActivity } = this.props;
		this.setNotifications(blockActivity);
	}

	setNotifications = blockList => {
		const notificationsArr = [];
		if (blockList !== undefined) {
			for (let i = 0; i < 3 && blockList && blockList[i]; i += 1) {
				const block = blockList[i];
				const notify = {
					title: `Block ${block.blocknum} `,
					type: 'block',
					time: block.createdt,
					txcount: block.txcount,
					datahash: block.datahash,
					blockhash: block.blockhash,
					channelName: block.channelname
				};
				notificationsArr.push(notify);
			}
		}
		this.setState({ notifications: notificationsArr });
	};

	handleTransactionClick = (e, txhash) => {
		e.preventDefault();
		if (txhash) {
			window.open(`#/transactions/${txhash}`, '_blank');
		}
	};

	renderTransactionTable = (transactionList, classes) => {
		const transactions = this.getTransactionListData();

		if (!transactions || transactions.length === 0) {
			return <div style={{ padding: '20px', textAlign: 'center', color: '#666666' }}>No transactions available</div>;
		}

		// Get latest 20 transactions
		const recentTransactions = transactions.slice(0, 20);

		const columns = [
			{
				Header: 'Tx Id',
				accessor: 'txhash',
				Cell: row => (
					<a
						href={`#/transactions/${row.value}`}
						className={classes.transactionLink}
						onClick={e => this.handleTransactionClick(e, row.value)}
						target="_blank"
						rel="noopener noreferrer"
					>
						{row.value ? `${row.value.slice(0, 8)}...${row.value.slice(-8)}` : '-'}
					</a>
				),
				minWidth: 200,
				style: { textAlign: 'left' }
			},
			{
				Header: 'Channel Name',
				accessor: 'channelname',
				minWidth: 150,
				style: { textAlign: 'left' }
			},
			{
				Header: 'Type',
				accessor: 'type',
				minWidth: 120,
				style: { textAlign: 'left' }
			},
			{
				Header: 'Created',
				accessor: 'createdt',
				Cell: row => {
					if (!row.value) return '-';
					const date = new Date(row.value);
					return date.toLocaleString();
				},
				minWidth: 200,
				style: { textAlign: 'left' }
			}
		];

		return (
			<div className={`${classes.tableWrapper} ${classes.tableStyles}`}>
				<ReactTable
					data={recentTransactions}
					columns={columns}
					pageSize={20}
					showPagination={false}
					minRows={0}
					style={{ height: '500px', width: '100%' }}
				/>
			</div>
		);
	};

	getTransactionListData = () => {
		const { transactionList } = this.props;
		if (Array.isArray(transactionList)) {
			return transactionList;
		} else if (transactionList && transactionList.rows && Array.isArray(transactionList.rows)) {
			return transactionList.rows;
		} else if (transactionList && transactionList.data && Array.isArray(transactionList.data)) {
			return transactionList.data;
		}
		return [];
	};

	calculateStats = () => {
		const { dashStats } = this.props;
		const transactions = this.getTransactionListData();
		const now = new Date();
		const yesterday = new Date(now);
		yesterday.setDate(yesterday.getDate() - 1);
		const last24Hours = new Date(now);
		last24Hours.setHours(last24Hours.getHours() - 24);

		// TOTAL values should reflect the whole network, not only recent list
		let totalTx = 0;
		let yesterdayTx = 0;
		let last24HoursTx = 0;
		let totalOperations = 0;
		let yesterdayOperations = 0;
		let last24HoursOperations = 0;

		// Use global total from dashStats when available
		if (dashStats && dashStats.txCount !== undefined) {
			const parsedTotal = parseInt(dashStats.txCount, 10);
			totalTx = Number.isNaN(parsedTotal) ? 0 : parsedTotal;
			totalOperations = totalTx;
		}

		// Recent metrics are computed from the fetched transaction list
		if (transactions && transactions.length > 0) {
			transactions.forEach(tx => {
				if (!tx.createdt) return;

				const txDate = new Date(tx.createdt);

				if (txDate >= last24Hours) {
					last24HoursTx += 1;
					last24HoursOperations += 1;
				}

				const yesterdayEnd = new Date(yesterday.getTime() + 24 * 60 * 60 * 1000);
				if (txDate >= yesterday && txDate < yesterdayEnd) {
					yesterdayTx += 1;
					yesterdayOperations += 1;
				}
			});
		}

		return {
			totalTx: totalTx || 0,
			yesterdayTx: yesterdayTx || '0',
			last24HoursTx: last24HoursTx || '0',
			blocks: (dashStats && dashStats.latestBlock) || '0',
			totalOperations: totalOperations || 0,
			yesterdayOperations: yesterdayOperations || '0',
			last24HoursOperations: last24HoursOperations || '0',
			nodes: (dashStats && dashStats.peerCount) || '0'
		};
	};

	handleSearch = async (e) => {
		e.preventDefault();
		const searchValue = this.state.searchValue || '';
		if (!searchValue) {
			this.setState({ searchError: 'Please enter a search value' });
			return;
		}

		this.setState({ searchError: '', searchDialogOpen: false });
		const { getTxnList, getBlockSearch, currentChannel } = this.props;
		
		// Try transaction first
		await getTxnList(currentChannel, searchValue);
		
		// Check if transaction found
		if (this.props.txnList && typeof this.props.txnList !== 'string' && this.props.txnList.txhash) {
			this.setState({ searchDialogOpen: true });
			return;
		}
		
		// If not found, try block
		await getBlockSearch(currentChannel, searchValue);
		if (this.props.blockSearch && typeof this.props.blockSearch !== 'string' && this.props.blockSearch.blockhash) {
			this.setState({ searchDialogOpen: true });
			return;
		}
		
		// Not found
		this.setState({ searchError: 'Transaction or Block not found' });
	};

	handleCloseSearchDialog = () => {
		this.setState({ searchDialogOpen: false, searchValue: '' });
	};

	render() {
		const {
			dashStats,
			peerList,
			txnList,
			blockSearch,
			blockActivity,
			transactionByOrg,
			transactionList
		} = this.props;
		const { hasDbError, notifications } = this.state;
		var searchError = '';
		if (typeof txnList === 'string') {
			searchError = 'Txn not found';
		} else if (typeof blockSearch === 'string') {
			searchError = 'Block not found';
		}
		if (hasDbError) {
			return (
				<div
					style={{
						height: '100vh',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center'
					}}
				>
					<h1>
						Please verify your network configuration, database configuration and try
						again
					</h1>
				</div>
			);
		}
		const { classes } = this.props;
		const stats = this.calculateStats();
		
		return (
			<div className={classes.background}>
				<div className={classes.view}>
					{/* Search Bar */}
					<div className={classes.searchContainer}>
						<form onSubmit={this.handleSearch} className={classes.searchBar}>
							<FontAwesome name="search" className={classes.searchIcon} />
							<input
								type="text"
								placeholder="Tx hash"
								className={classes.searchInput}
								value={this.state?.searchValue || ''}
								onChange={(e) => this.setState({ searchValue: e.target.value })}
							/>
							<button type="submit" className={classes.searchButton}>
								Search
							</button>
						</form>
					</div>

					{/* Statistics Grid */}
					<div className={classes.statsGrid}>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.totalTx)}</div>
							<div className={classes.statLabel}>Total Tx</div>
						</Card>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.yesterdayTx)}</div>
							<div className={classes.statLabel}>Yesterday's Tx</div>
						</Card>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.last24HoursTx)}</div>
							<div className={classes.statLabel}>Last 24 hour's Tx</div>
						</Card>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.blocks)}</div>
							<div className={classes.statLabel}>Blocks</div>
						</Card>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.totalOperations)}</div>
							<div className={classes.statLabel}>Total Operations</div>
						</Card>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.yesterdayOperations)}</div>
							<div className={classes.statLabel}>Yesterday's Operations</div>
						</Card>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.last24HoursOperations)}</div>
							<div className={classes.statLabel}>Last 24 hour's Operations</div>
						</Card>
						<Card className={classes.statCard}>
							<div className={classes.statValue}>{this.formatNumber(stats.nodes)}</div>
							<div className={classes.statLabel}>Nodes</div>
						</Card>
					</div>
					<Row>
						<Col sm="6">
							<Card className={classes.section}>
								<TimelineStream
									notifications={notifications}
									blockList={blockActivity}
								/>
							</Card>
						</Col>
						<Col sm="6">
							<Card className={classes.section}>
								<ChartStats />
							</Card>
						</Col>
					</Row>
					<Row>
						<Col sm="12">
							<Card className={classes.transactionTable}>
								<div className={classes.tableTitle}>Recent Transactions</div>
								{this.renderTransactionTable(transactionList, classes)}
							</Card>
						</Col>
					</Row>

					{/* Search Error */}
					{this.state.searchError && (
						<div style={{ 
							marginTop: '10px', 
							padding: '10px', 
							backgroundColor: '#ffebee', 
							color: '#c62828',
							borderRadius: '4px',
							textAlign: 'center',
							maxWidth: '800px',
							margin: '10px auto 0'
						}}>
							{this.state.searchError}
						</div>
					)}

					{/* Search Result Dialog */}
					<Dialog
						open={this.state.searchDialogOpen}
						onClose={this.handleCloseSearchDialog}
						fullWidth
						maxWidth="md"
					>
						{this.props.blockSearch && typeof this.props.blockSearch !== 'string' && this.props.blockSearch.blockhash ? (
							<BlockView 
								blockHash={this.props.blockSearch} 
								onClose={this.handleCloseSearchDialog} 
							/>
						) : (
							<TransactionView 
								transaction={this.props.txnList} 
								onClose={this.handleCloseSearchDialog} 
							/>
						)}
					</Dialog>
				</div>
			</div>
		);
	}
}

DashboardView.propTypes = {
	blockListSearch: blockListSearchType.isRequired,
	dashStats: dashStatsType.isRequired,
	peerList: peerListType.isRequired,
	txnList: txnListType.isRequired,
	blockSearch: blockSearchType.isRequired,
	transactionByOrg: transactionByOrgType.isRequired,
	transactionList: transactionListType
};

const mapStateToProps = state => {
	return {
		currentChannel: currentChannelSelector(state)
	};
};
const mapDispatchToProps = {
	getTxnList: txnList,
	getBlockSearch: blockSearch,
	getTransactionList: transactionList
};
const connectedComponent = connect(
	mapStateToProps,
	mapDispatchToProps
)(DashboardView);
export default withStyles(styles)(connectedComponent);
